/**
 * Maps raw Umbraco Delivery API v2 JSON (`eCEQuizContent` and nested block lists) into `QuizConfig`.
 * Accepts common wrapper shapes (`items`, `content`, `properties`, `contentData`).
 */
import type { QuizAnswer, QuizConfig, QuizQuestion, QuizResult } from './types';

type UnknownRecord = Record<string, unknown>;

function asRecord(v: unknown): UnknownRecord | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as UnknownRecord) : null;
}

/** Reads block-list children from `items` or legacy `contentData`. */
function getItems(blockList: unknown): unknown[] {
  const r = asRecord(blockList);
  if (!r) return [];
  const items = r.items;
  if (Array.isArray(items)) return items;
  const contentData = r.contentData;
  if (Array.isArray(contentData)) return contentData;
  return [];
}

/** Resolves the property bag for one block-list row (handles `content` nesting). */
function blockProperties(item: unknown): UnknownRecord {
  const r = asRecord(item);
  if (!r) return {};
  const content = r.content ?? item;
  const c = asRecord(content);
  if (!c) return {};
  const props = c.properties;
  const p = asRecord(props);
  return p ?? c;
}

/** First non-empty string among alias keys (CMS field renames). */
function readString(obj: UnknownRecord, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/** Parses first numeric field among keys (number or numeric string). */
function readNumber(obj: UnknownRecord, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/** One `eCEAnswers` row: text, result label, weight ≥ 1. */
function parseAnswer(item: unknown): QuizAnswer | null {
  const o = blockProperties(item);
  const text = readString(o, 'eCEAnswerText');
  const resultLabel = readString(o, 'eCEAnswerResult');
  const weight = readNumber(o, 'eCEAnswerResultWeight');
  if (!text || !resultLabel || weight === null || weight < 1) return null;
  return { text, resultLabel, weight: Math.floor(weight) };
}

/** One `quizQuestions` row: prompt + ≥ 2 valid answers. */
function parseQuestion(item: unknown): QuizQuestion | null {
  const o = blockProperties(item);
  const text = readString(o, 'eCEQuestionText');
  const rawAnswers = o.eCEAnswers;
  const items = getItems(rawAnswers);
  const answers: QuizAnswer[] = [];
  for (const it of items) {
    const a = parseAnswer(it);
    if (a) answers.push(a);
  }
  if (!text || answers.length < 2) return null;
  return { text, answers };
}

/** One `quizResults` row: display name + body copy. */
function parseResult(item: unknown): QuizResult | null {
  const o = blockProperties(item);
  const name = readString(o, 'eCEResultName');
  const description = readString(o, 'eCEResultDescription');
  if (!name || !description) return null;
  return { name, description };
}

/** Properties on the Delivery content item (unwrap `properties` when present). */
function topLevelProperties(item: unknown): UnknownRecord {
  const r = asRecord(item);
  if (!r) return {};
  const props = r.properties;
  const p = asRecord(props);
  return p ?? r;
}

/**
 * Extract first eCEQuizContent item from a Delivery API JSON body and validate.
 * Fails closed (returns null) if shape is wrong or question count ≠ 7.
 */
export function parseQuizDeliveryItem(body: unknown): QuizConfig | null {
  const root = asRecord(body);
  if (!root) return null;
  const items = root.items;
  if (!Array.isArray(items) || items.length === 0) return null;

  const first = items[0];
  const props = topLevelProperties(first);

  const quizTitle = readString(props, 'quizTitle');
  const articleUrl = readString(props, 'articleURL', 'articleUrl');
  if (!quizTitle || !articleUrl) return null;

  const qItems = getItems(props.quizQuestions);
  const questions: QuizQuestion[] = [];
  for (const it of qItems) {
    const q = parseQuestion(it);
    if (q) questions.push(q);
  }
  if (questions.length !== 7) return null;

  const rItems = getItems(props.quizResults);
  const results: QuizResult[] = [];
  for (const it of rItems) {
    const res = parseResult(it);
    if (res) results.push(res);
  }
  if (results.length < 2) return null;

  const resultNames = new Set(results.map((r) => r.name));
  for (const q of questions) {
    for (const a of q.answers) {
      if (!resultNames.has(a.resultLabel)) return null;
    }
  }

  return {
    quizTitle,
    articleUrl,
    questions: questions as QuizConfig['questions'],
    results,
  };
}
